<template>
  <link
    href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap"
    rel='stylesheet'
  >
  <ion-app>
    <portal-target name="sideblade" />
    <ion-router-outlet />
  </ion-app>
</template>

<script setup lang="ts">
import {
  IonApp,
  IonRouterOutlet
} from '@ionic/vue'
import { onMounted, onUnmounted } from 'vue'
import { useFirebaseToken } from '@/assets/ts/composables/useFirebaseToken'
import { getMessaging, getToken } from 'firebase/messaging'
import Http from '@/assets/ts/classes/Http'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { Preferences } from '@capacitor/preferences'
import { useRouter } from 'vue-router'
import { initializeFirebase } from './firebase'

const { stopTokenRefresh } = useFirebaseToken()
const router = useRouter()

const checkAuth = async (): Promise<boolean> => {
  try {
    const token = await Preferences.get({ key: 'abilibee-token' });
    if (!token.value) {
      router.push('/login');
      return false;
    }

    // Initialize Firebase for all platforms
    const app = await initializeFirebase();

    // Handle notifications based on platform
    if (Capacitor.isNativePlatform()) {
      // For iOS and Android
      const hasPermission = await checkNotificationPermission();
      if (hasPermission) {
        await registerPushNotifications();
      }
    } else {
      // For web
      const messaging = getMessaging(app);
      const fcmToken = await getToken(messaging, {
        vapidKey: 'BIThmWIafBQGiakQArXR0GWbSdZRnc5m7vcgbGk-hNFG1E1GvohSI7yUantZwNUI_qEG9GOkVwnbpGqfNHqTHto'
      });
      console.log('FCM Token:', fcmToken);
      await sendTokenToBackend(fcmToken);
    }

    return true;
  } catch (error) {
    console.error('Error checking auth:', error);
    router.push('/login');
    return false;
  }
};

// Function to send token to backend with debounce
let lastTokenSent = '';
let tokenSendTimeout: NodeJS.Timeout | null = null;

const sendTokenToBackend = async (token: string) => {
  // If token is the same as the last sent, do nothing
  if (token === lastTokenSent) {
    return;
  }

  // Clear previous timeout if exists
  if (tokenSendTimeout) {
    clearTimeout(tokenSendTimeout);
  }

  // Set new timeout to send token
  tokenSendTimeout = setTimeout(async () => {
    try {
      // Save token locally
      await Preferences.set({ key: 'firebase-token', value: token });

      // Send token to backend
      await Http.sendFirebaseToken(token);

      // Update last sent token
      lastTokenSent = token;
    } catch (error) {
      console.error('Error sending token to backend:', error);
    }
  }, 1000); // Wait 1 second before sending
};

const checkNotificationPermission = async (): Promise<boolean> => {
  try {
    if (Capacitor.isNativePlatform()) {
      const permission = await PushNotifications.checkPermissions();

      if (permission.receive === 'prompt') {
        const newPermission = await PushNotifications.requestPermissions();
        return newPermission.receive === 'granted';
      }

      return permission.receive === 'granted';
    } else {
      // For web
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return false;
  }
};

const registerPushNotifications = async (): Promise<void> => {
  try {

    if (Capacitor.isNativePlatform()) {
      // Check permissions first
      const permission = await PushNotifications.checkPermissions();

      if (permission.receive !== 'granted') {
        const newPermission = await PushNotifications.requestPermissions();

        if (newPermission.receive !== 'granted') {
          console.error('Push notification permissions not granted');
          return;
        }
      }

      // Listener for FCM token received from AppDelegate
      window.addEventListener('fcmTokenReceived', async (event: any) => {
        await sendTokenToBackend(event.detail.token);
      });

      // Registration listener
      PushNotifications.addListener('registration', async (token) => {
        if (token.value) {
          await sendTokenToBackend(token.value);
        } else {
          console.error('Empty registration token received');
        }
      });

      // Registration error listener
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Error on registration:', error);
        // Retry registration after error
        setTimeout(async () => {
          try {
            await PushNotifications.register();
          } catch (retryError) {
            console.error('Error in registration retry:', retryError);
          }
        }, 5000);
      });

      // Register for push notifications in iOS/Android
      try {
        await PushNotifications.register();
        console.log('PushNotifications.register() called successfully');
      } catch (registerError) {
        console.error('Error during PushNotifications.register():', registerError);
        return;
      }

      // Check if we already have a saved token
      const savedToken = await Preferences.get({ key: 'firebase-token' });
      if (savedToken.value) {
        await sendTokenToBackend(savedToken.value);
      }
    }
  } catch (error) {
    console.error('Error in registerPushNotifications:', error);
  }
};

onMounted(async (): Promise<void> => {
  try {
    const isAuthenticated = await checkAuth();
    if (!isAuthenticated) return;
  } catch (error) {
    console.error('Error in onMounted:', error);
  }
});

onUnmounted(() => {
  stopTokenRefresh()
})
</script>